// 43026 — shared "Use recipe / Save recipe" control for feed itemization points.
// Props:
//   lines      current itemization rows (array)
//   setLines   replace the rows (applies a recipe verbatim)
//   qtyKey     the qty field name on the rows — 'qty' (edit-time editor) or 'qty_kg' (entry-time forms)
//   provenance { source_po_ref? } — where a Save originates
// A recipe is a shortcut only: applying just fills the rows; nothing is stored against the purchase.
import ShellModal, { ConfirmModal } from '../shell/drawer/ShellModal';
import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { IcoLn } from '../shell/ActionIcons';
import { Emo } from '../shell/EmojiIcons';
import { useTranslation } from 'react-i18next';
import EnumSelect from './EnumSelect';
import { TextInput } from './inputs/Input';

const btn = { border: '1px solid var(--border)', background: 'var(--surface)', color: 'var(--text)', fontSize: 12, fontWeight: 600, padding: '5px 11px', borderRadius: 8, cursor: 'pointer', fontFamily: 'inherit' };

export default function RecipeControl({ lines, setLines, qtyKey = 'qty', provenance = {}, disabled = false }) {
  const { t } = useTranslation('dataEntry');
  const [recipes, setRecipes] = useState([]);
  const [overwrite, setOverwrite] = useState(null);   // recipe pending apply-confirm
  const [msg, setMsg] = useState('');
  const [saveOpen, setSaveOpen] = useState(false);
  const [saveName, setSaveName] = useState('');
  const [saveCols, setSaveCols] = useState({ item_name: true, unit: true, unit_price: true, qty: true });
  const reload = () => api.getFeedRecipes('available').then(r => setRecipes(Array.isArray(r) ? r : [])).catch(() => {});
  useEffect(() => { reload(); }, []);

  const nonEmpty = l => !!((l.item_name && String(l.item_name).trim()) || (l.unit_price !== '' && l.unit_price != null) || (l[qtyKey] !== '' && l[qtyKey] != null));
  const hasContent = (lines || []).some(nonEmpty);

  const toRows = rec => (rec.lines || []).map(l => ({
    item_name: l.item_name == null ? '' : l.item_name,
    unit: l.unit == null ? 'kg' : l.unit,
    [qtyKey]: l.qty == null ? '' : String(l.qty),
    unit_price: l.unit_price == null ? '' : String(l.unit_price),
  }));
  // fb-176: reset = empty the displayed recipe and go back to ONE empty row (does not touch saved recipes).
  const blankRow = () => ({ item_name: '', unit: 'kg', [qtyKey]: '', unit_price: '' });
  const canReset = !disabled && ((lines || []).length > 1 || hasContent);
  const reset = () => { setLines([blankRow()]); setMsg(''); };
  const apply = rec => { setLines(toRows(rec)); setMsg(t('deUi.applied', { name: rec.name })); };
  const pick = id => { if (!id) return; api.getFeedRecipe(id).then(rec => { if (hasContent) setOverwrite(rec); else apply(rec); }).catch(e => setMsg(e.message || 'Could not load recipe.')); };

  const openSave = () => { setSaveName(''); setSaveCols({ item_name: true, unit: true, unit_price: true, qty: true }); setSaveOpen(true); };
  const doSave = () => {
    if (!saveName.trim()) return;
    const payload = {
      name: saveName.trim(),
      origin_kind: provenance.source_po_ref ? 'saved_from_po' : 'saved_from_entry',
      source_po_ref: provenance.source_po_ref || null,
      lines: (lines || []).filter(nonEmpty).map(l => ({
        item_name: saveCols.item_name ? l.item_name : null,
        unit: saveCols.unit ? l.unit : null,
        unit_price: saveCols.unit_price ? l.unit_price : null,
        qty: saveCols.qty ? l[qtyKey] : null,
      })),
    };
    api.createFeedRecipe(payload).then(() => { setMsg(t('deUi.savedRecipe', { v1: saveName.trim() })); setSaveOpen(false); reload(); }).catch(e => setMsg(e.message || 'Save failed.'));
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
      <EnumSelect bare disabled={disabled} value="" onChange={v => { pick(v); v = ''; }} title={t('deUi.applyASavedRecipeTo')} wrapStyle={{ maxWidth: 240 }}>
        <option value=""><Emo name="clipboard" /> {t('deUi.useRecipe')}</option>
        {recipes.map(r => <option key={r.id} value={r.id}>{r.name} ({r.line_count})</option>)}
        {recipes.length === 0 && <option value="" disabled>{t('deUi.noRecipesYet')}</option>}
      </EnumSelect>
      <button type="button" disabled={disabled || !hasContent} title={hasContent ? t('deUi.saveTheCurrentItemizationAs') : t('deUi.enterSomeComponentsFirst')}
        onClick={openSave} style={{ ...btn, opacity: (disabled || !hasContent) ? 0.5 : 1, cursor: (disabled || !hasContent) ? 'not-allowed' : 'pointer' }}><IcoLn name="save" /> {t('deUi.saveRecipe')}</button>
      <button type="button" disabled={!canReset} title={canReset ? t('deUi.clearAllComponentsAndGo') : t('deUi.nothingToReset')}
        onClick={reset} style={{ ...btn, opacity: canReset ? 1 : 0.5, cursor: canReset ? 'pointer' : 'not-allowed' }}><IcoLn name="refresh" /> {t('common:stgUi.reset')}</button>
      {msg && <span style={{ fontSize: 11.5, color: 'var(--accent)' }}>{msg}</span>}

      {overwrite && (
        <ConfirmModal title={t('deUi.overwriteCurrentItemization')} cancelLabel={t('actions.cancel')} confirmLabel={t('deUi.overwrite')}
          message={<>{t('deUi.applying')} <b>“{overwrite.name}”</b> {t('deUi.willReplaceTheItemizationLines')}</>}
          onCancel={() => setOverwrite(null)} onConfirm={() => { apply(overwrite); setOverwrite(null); }} />
      )}

      {saveOpen && (
        <ShellModal title={t('deUi.saveAsRecipe')} size="sm" onClose={() => setSaveOpen(false)} closeLabel={t('actions.cancel')}
          primary={<button className="btn success" onClick={doSave} disabled={!saveName.trim()}>{t('deUi.saveRecipe')}</button>}>
            <TextInput autoFocus placeholder={t('deUi.recipeName')} value={saveName} onChange={e => setSaveName(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') doSave(); }}
              style={{ border: '1px solid var(--border)', borderRadius: 7, padding: '7px 10px', font: 'inherit', fontSize: 13, width: '100%', boxSizing: 'border-box', marginBottom: 14 }} />
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.04em', textTransform: 'uppercase', color: 'var(--text-3)', marginBottom: 7 }}>{t('deUi.columnsToSave')}</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 7, marginBottom: 18 }}>
              {[['item_name', 'Item'], ['unit', 'Unit'], ['unit_price', 'Unit price'], ['qty', 'Qty']].map(([k, lbl]) => (
                <label key={k} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer', color: 'var(--text)' }}>
                  <input type="checkbox" checked={saveCols[k]} onChange={() => setSaveCols(c => ({ ...c, [k]: !c[k] }))} style={{ accentColor: 'var(--accent)', width: 15, height: 15 }} /> {lbl}
                </label>
              ))}
            </div>
        </ShellModal>
      )}
    </div>
  );
}
